import React from 'react';
import { Color } from '../../types';

type PositionX = 'left' | 'center' | 'right';
type PositionY = 'top' | 'middle' | 'bottom';

type CssPositions = 'top' | 'left' | 'bottom' | 'right' | 'transform';

type PositionStyle = Pick<React.CSSProperties, CssPositions>;

type PositionStyles = {
	[x in PositionX]: {
		[y in PositionY]: PositionStyle;
	};
};

const centerY: PositionStyle = {
	top: '50%',
	transform: 'translateY(0, -50%, 0)'
};

const centerX: PositionStyle = {
	left: '50%',
	transform: 'translate3d(-50%, 0, 0)'
};

const styles = (offsetX: number, offsetY: number): PositionStyles => {
	const edgeX = `calc(100% - ${offsetX}px)`;
	const edgeY = `calc(100% - ${offsetY}px)`;
	const top = edgeY;
	const right = edgeX;
	const bottom = edgeY;
	const left = edgeX;

	return {
		left: {
			top: {
				right,
				bottom
			},
			middle: {
				right,
				...centerY
			},
			bottom: {
				right,
				top
			}
		},
		center: {
			top: {
				...centerX,
				bottom
			},
			middle: {
				...centerX,
				...centerY,
				transform: 'translate3d(-50%, -50%, 0)'
			},
			bottom: {
				...centerX,
				top
			}
		},
		right: {
			top: {
				left,
				bottom
			},
			middle: {
				left,
				...centerY
			},
			bottom: {
				left,
				top
			}
		}
	};
};

export type NumberIndicatorProps = Omit<
	React.HTMLAttributes<HTMLDivElement>,
	'children'
> & {
	children: number;
	size?: number;
	color?: Color;
	x?: PositionX;
	y?: PositionY;
	noOffset?: boolean;
	offset?: number;
	offsetX?: number;
	offsetY?: number;
	style?: Omit<
		React.CSSProperties,
		CssPositions | 'position' | 'width' | 'height' | 'fontSize'
	>;
};

export const NumberIndicator: React.FC<NumberIndicatorProps> = ({
	children,
	size = 20,
	color = 'primary',
	x = 'right',
	y = 'top',
	className,
	style,
	offset = size / 2,
	offsetX = offset,
	offsetY = offset,
	...props
}) => {
	return (
		<div
			className={`bg-${color} text-center rounded-circle number-indicator${
				className ? ` ${className}` : ''
			}`}
			style={{
				...style,
				width: size,
				height: size,
				position: 'absolute',
				...styles(offsetX, offsetY)[x][y]
			}}
			{...props}
		>
			<div
				className="d-flex align-items-center justify-content-center text-white font-weight-bold w-100 h-100"
				style={{
					fontSize: size / 1.5,
					lineHeight: 1
				}}
			>
				{children}
			</div>
		</div>
	);
};
